import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, UploadCloud, Check, Loader2 } from "lucide-react";
import { LOGO } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDIA_STATES = ["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Delhi"];
const JOB_ROLES = ["CCTV Technician","Network Engineer","IT Hardware Technician","Security Systems Installer","Sales Executive","Project Manager","Service Engineer","Admin/Operations","Finance Executive","Social Media Marketing","Accounts Executive","Others"];
const DEPARTMENTS = ["Technical","Sales","Admin/Operations","Projects","Finance","Marketing","Others"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const MARITAL = ["Married","Unmarried / Single","Divorced","Widowed"];
const NOTICE = ["Immediate","7 Days","15 Days","30 Days (1 Month)","45 Days","60 Days (2 Months)","90 Days (3 Months)","More Than 90 Days"];
const CUR_YEAR = Math.max(new Date().getFullYear(), 2026);
const YEARS_DOB = Array.from({ length: 71 }, (_, i) => CUR_YEAR - i);
const YEARS_EXP = Array.from({ length: CUR_YEAR - 1949 }, (_, i) => CUR_YEAR - i);
const DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0"));

const EMPTY = {
  first_name: "", middle_name: "", surname: "", email: "", father_name: "", mother_name: "",
  present_address: "", present_country: "", present_state: "", present_state_free: "", present_country_free: "", present_pincode: "",
  same_address: false,
  permanent_address: "", permanent_country: "", permanent_state: "", permanent_state_free: "", permanent_country_free: "", permanent_pincode: "",
  dob_day: "", dob_month: "", dob_year: "", gender: "", marital_status: "", spouse_name: "", current_location: "",
  mobile_number: "", emergency_contact: "",
  preferred_location_1: "Ernakulam", position: "", department: "",
  experience_status: "", current_ctc: "", expected_ctc: "", notice_period: "", source: "",
  acknowledgement: false, whatsapp_consent: false,
};
const EMPTY_QUAL = { degree: "", year: "", college: "", percentage: "", courses: "" };
const EMPTY_EXP = { employer: "", designation: "", from_month: "", from_year: "", to_month: "", to_year: "", current: false };

const FILE_FIELDS = { photo_file: "Passport photo", resume_file: "Resume", cover_letter_file: "Cover letter" };
const FIELD_LABELS = {
  ...FILE_FIELDS,
  first_name: "First name", surname: "Surname", email: "Email address",
  mobile_number: "Mobile number", emergency_contact: "Emergency contact",
  present_pincode: "Present pincode", permanent_pincode: "Permanent pincode",
};

// Turn any submit failure into clear, candidate-friendly messages.
const parseSubmitError = (err) => {
  const resp = err?.response;
  if (!resp) {
    return ["We couldn't reach the server — please check your internet connection and tap Submit again."];
  }
  if (resp.status >= 500) {
    return ["The server had a problem processing your application. Please wait a moment and tap Submit again."];
  }
  const detail = resp.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return [detail];
  }
  if (Array.isArray(detail) && detail.length) {
    return detail.map((d) => {
      const loc = Array.isArray(d?.loc) ? d.loc[d.loc.length - 1] : "";
      const nice = FIELD_LABELS[loc] || (typeof loc === "string" ? loc.replace(/_/g, " ") : "A field");
      const isFile = Object.prototype.hasOwnProperty.call(FILE_FIELDS, loc);
      const msg = d?.msg || "is invalid";
      return `${nice}: ${msg}${isFile ? " — please re-attach the file and submit again." : ""}`;
    });
  }
  return ["Could not submit. Please review your details, or email your CV to careers@nexsecure.in"];
};

const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

// Shrink an oversized photo entirely in the browser so it always fits the size limit.
const compressImage = (file, maxBytes) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const MAXDIM = 1200;
      let { width, height } = img;
      if (width > MAXDIM || height > MAXDIM) {
        const scale = MAXDIM / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff"; // flatten any transparency (PNG → JPEG)
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      let q = 0.9;
      const attempt = () => {
        canvas.toBlob(
          (blob) => {
            if (!blob) { reject(new Error("encode-failed")); return; }
            if (blob.size <= maxBytes || q <= 0.4) {
              const name = `${file.name.replace(/\.[^.]+$/, "")}.jpg`;
              resolve(new File([blob], name, { type: "image/jpeg" }));
            } else {
              q -= 0.1;
              attempt();
            }
          },
          "image/jpeg",
          q,
        );
      };
      attempt();
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("load-failed")); };
    img.src = url;
  });

// Optional in-browser passport (3:4) cropper — drag to reposition, slider to zoom.
const PassportCropModal = ({ file, onCancel, onApply }) => {
  const BOX_W = 240, BOX_H = 320;
  const [img, setImg] = useState(null);
  const [scale, setScale] = useState(1);
  const [minScale, setMinScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const dragRef = useRef(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      const ms = Math.max(BOX_W / image.width, BOX_H / image.height);
      setImg(image);
      setMinScale(ms);
      setScale(ms);
      setPos({ x: (BOX_W - image.width * ms) / 2, y: (BOX_H - image.height * ms) / 2 });
    };
    image.src = url;
  }, [file]);

  const clamp = (p, s) => {
    if (!img) return p;
    const dispW = img.width * s, dispH = img.height * s;
    return { x: Math.min(0, Math.max(BOX_W - dispW, p.x)), y: Math.min(0, Math.max(BOX_H - dispH, p.y)) };
  };
  const onScale = (e) => { const s = parseFloat(e.target.value); setScale(s); setPos((p) => clamp(p, s)); };
  const onPointerDown = (e) => { dragRef.current = { sx: e.clientX, sy: e.clientY, px: pos.x, py: pos.y }; e.currentTarget.setPointerCapture?.(e.pointerId); };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    setPos(clamp({ x: dragRef.current.px + (e.clientX - dragRef.current.sx), y: dragRef.current.py + (e.clientY - dragRef.current.sy) }, scale));
  };
  const onPointerUp = () => { dragRef.current = null; };

  const apply = () => {
    if (!img) return;
    setBusy(true);
    const OUT_W = 600, OUT_H = 800;
    const canvas = document.createElement("canvas");
    canvas.width = OUT_W; canvas.height = OUT_H;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, OUT_W, OUT_H);
    const sx = (-pos.x) / scale, sy = (-pos.y) / scale, sw = BOX_W / scale, sh = BOX_H / scale;
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, OUT_W, OUT_H);
    canvas.toBlob((blob) => {
      const name = `${file.name.replace(/\.[^.]+$/, "")}-passport.jpg`;
      onApply(new File([blob], name, { type: "image/jpeg" }));
      setBusy(false);
    }, "image/jpeg", 0.9);
  };

  return (
    <div data-lenis-prevent className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4" data-testid="caf-crop-modal">
      <div className="bg-[#0e1b30] border border-white/15 p-5 w-full max-w-sm">
        <p className="font-marcellus text-lg text-white mb-3">Crop to passport size (3:4)</p>
        <div
          className="relative overflow-hidden border border-nx-goldlight/50 mx-auto touch-none select-none cursor-move bg-black/40"
          style={{ width: BOX_W, height: BOX_H }}
          onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}
        >
          {img && (
            <img src={img.src} alt="" draggable="false" style={{ position: "absolute", left: pos.x, top: pos.y, width: img.width * scale, height: img.height * scale, maxWidth: "none" }} />
          )}
        </div>
        <label className="block text-[11px] text-slate-400 mt-4 mb-1">Zoom</label>
        <input type="range" min={minScale} max={minScale * 3} step={0.01} value={scale} onChange={onScale} className="w-full accent-nx-goldlight" data-testid="caf-crop-zoom" />
        <p className="text-[11px] text-slate-500 mt-1">Drag the photo to reposition it inside the frame.</p>
        <div className="flex gap-3 mt-4">
          <button type="button" onClick={onCancel} className="flex-1 border border-white/20 text-slate-300 hover:text-white text-sm py-2.5 transition-colors" data-testid="caf-crop-cancel">Cancel</button>
          <button type="button" onClick={apply} disabled={busy || !img} className="flex-1 bg-nx-goldlight hover:bg-nx-gold text-nx-navy font-semibold text-sm py-2.5 transition-colors disabled:opacity-60" data-testid="caf-crop-apply">{busy ? "Cropping…" : "Apply crop"}</button>
        </div>
      </div>
    </div>
  );
};

const CareerApplicationForm = ({ open, onClose }) => {
  const [f, setF] = useState(EMPTY);
  const [quals, setQuals] = useState([{ ...EMPTY_QUAL }]);
  const [exps, setExps] = useState([{ ...EMPTY_EXP }]);
  const [files, setFiles] = useState({ resume: null, photo: null, cover: null });
  const [errors, setErrors] = useState([]);
  const [sending, setSending] = useState(false);
  const [doneRef, setDoneRef] = useState("");
  const [otp, setOtp] = useState({ sent: false, code: "", verified: false, error: "", sending: false, verifying: false, resendIn: 0 });
  const [inlineErr, setInlineErr] = useState({ email: "", mobile_number: "", emergency_contact: "" });
  const [photoUrl, setPhotoUrl] = useState("");
  const [compressing, setCompressing] = useState(false);
  const [photoNote, setPhotoNote] = useState("");
  const [cropFile, setCropFile] = useState(null);
  const [draftFound, setDraftFound] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedNote, setDraftSavedNote] = useState("");
  const resendTimer = useRef(null);

  useEffect(() => {
    if (!files.photo) { setPhotoUrl(""); setPhotoNote(""); return; }
    const url = URL.createObjectURL(files.photo);
    setPhotoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [files.photo]);

  const emailValid = (v) => /^\S+@\S+\.\S+$/.test(v);
  const resetOtp = () => {
    clearInterval(resendTimer.current);
    setOtp({ sent: false, code: "", verified: false, error: "", sending: false, verifying: false, resendIn: 0 });
  };

  useEffect(() => () => clearInterval(resendTimer.current), []);

  const startResendTimer = (secs) => {
    clearInterval(resendTimer.current);
    setOtp((p) => ({ ...p, resendIn: secs }));
    resendTimer.current = setInterval(() => {
      setOtp((p) => {
        if (p.resendIn <= 1) { clearInterval(resendTimer.current); return { ...p, resendIn: 0 }; }
        return { ...p, resendIn: p.resendIn - 1 };
      });
    }, 1000);
  };

  const sendOtp = async () => {
    if (!emailValid(f.email)) {
      setInlineErr((p) => ({ ...p, email: "Enter a valid email address (must contain @ and domain)." }));
      return;
    }
    setOtp((p) => ({ ...p, sending: true, error: "" }));
    try {
      const { data } = await axios.post(`${API}/career-otp/send`, { email: f.email });
      setOtp((p) => ({ ...p, sending: false, sent: true, code: "" }));
      startResendTimer(data.retry_after ?? 60);
    } catch {
      setOtp((p) => ({ ...p, sending: false, error: "Could not send OTP. Check the email address and try again." }));
    }
  };

  const verifyOtp = async (code) => {
    const c = (code ?? otp.code).trim();
    if (c.length !== 6) return;
    setOtp((p) => ({ ...p, verifying: true, error: "" }));
    try {
      await axios.post(`${API}/career-otp/verify`, { email: f.email, otp: c });
      setOtp((p) => ({ ...p, verifying: false, verified: true, error: "" }));
      try {
        const { data } = await axios.get(`${API}/career-draft/${encodeURIComponent(f.email)}`);
        if (data?.draft?.data) setDraftFound(data.draft);
      } catch { /* no draft is fine */ }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setOtp((p) => ({ ...p, verifying: false, error: typeof detail === "string" ? detail : "Invalid OTP. Please try again." }));
    }
  };

  const onEmailChange = (e) => {
    set("email")(e);
    if (inlineErr.email) setInlineErr((p) => ({ ...p, email: "" }));
    if (otp.sent || otp.verified) resetOtp();
  };

  const onEmailBlur = () => {
    if (!f.email) return;
    if (!emailValid(f.email)) {
      setInlineErr((p) => ({ ...p, email: "Enter a valid email address (must contain @ and domain)." }));
      return;
    }
    setInlineErr((p) => ({ ...p, email: "" }));
  };

  const onPhoneBlur = (key) => () => {
    const v = f[key];
    if (!v) return;
    if (!/^\d{10}$/.test(v)) setInlineErr((p) => ({ ...p, [key]: "Enter exactly 10 digits (numbers only)." }));
    else if (key === "emergency_contact" && v === f.mobile_number) setInlineErr((p) => ({ ...p, emergency_contact: "Emergency contact cannot be the same as the mobile number." }));
    else setInlineErr((p) => ({ ...p, [key]: "" }));
  };

  if (!open) return null;

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setF((p) => {
      const next = { ...p, [k]: v };
      if (k === "same_address" && v) {
        next.permanent_address = next.present_address;
        next.permanent_country = next.present_country;
        next.permanent_state = next.present_state;
        next.permanent_state_free = next.present_state_free;
        next.permanent_country_free = next.present_country_free;
        next.permanent_pincode = next.present_pincode;
      }
      return next;
    });
  };
  const setUpper = (k) => (e) => { e.target.value = e.target.value.toUpperCase(); set(k)(e); };
  const setDigits = (k) => (e) => { e.target.value = e.target.value.replace(/[^0-9]/g, ""); set(k)(e); };

  const setQual = (i, k) => (e) => setQuals((q) => q.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)));
  const setExp = (i, k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setExps((x) => x.map((ex, j) => {
      if (j !== i) return ex;
      const nx = { ...ex, [k]: v };
      if (k === "current" && v) { nx.to_month = "Present"; nx.to_year = "Present"; }
      if (k === "current" && !v) { nx.to_month = ""; nx.to_year = ""; }
      return nx;
    }));
  };

  const personalComplete =
    f.first_name && f.surname && f.email && f.father_name && f.mother_name && f.current_location && f.marital_status &&
    f.dob_day && f.dob_month && f.dob_year && f.gender &&
    (f.marital_status !== "Married" || f.spouse_name) &&
    f.present_address && f.present_country &&
    (f.present_country === "India" ? (f.present_state && f.present_pincode.length === 6) : (f.present_state_free && f.present_country_free && f.present_pincode)) &&
    (f.same_address || (f.permanent_address && f.permanent_country &&
      (f.permanent_country === "India" ? (f.permanent_state && f.permanent_pincode.length === 6) : (f.permanent_state_free && f.permanent_country_free && f.permanent_pincode))));

  const pickFile = (key) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file after a fix
    if (!file) return;
    if (key === "resume" || key === "cover") {
      if (file.type !== "application/pdf") { setErrors([`${key === "resume" ? "Resume" : "Cover letter"} must be a PDF file.`]); return; }
      if (file.size > 5 * 1024 * 1024) { setErrors([`${key === "resume" ? "Resume" : "Cover letter"} must be smaller than 5 MB.`]); return; }
      setErrors([]);
      setFiles((p) => ({ ...p, [key]: file }));
      return;
    }
    if (key === "photo") {
      const MAX = 2 * 1024 * 1024;
      const okType = ["image/jpeg", "image/jpg", "image/png"].includes(file.type);
      if (!okType) { setErrors(["Passport photo must be a JPG, JPEG or PNG image."]); return; }
      setErrors([]);
      if (file.size <= MAX) {
        setPhotoNote("");
        setFiles((p) => ({ ...p, photo: file }));
        return;
      }
      // Oversized — shrink it in the browser so the candidate never hits the limit.
      setCompressing(true);
      try {
        const shrunk = await compressImage(file, MAX);
        setCompressing(false);
        if (shrunk.size > MAX) {
          setErrors(["This photo is still over 2 MB after optimising — please choose a smaller image."]);
          return;
        }
        setPhotoNote(`Photo optimised from ${fmtSize(file.size)} to ${fmtSize(shrunk.size)} to fit the 2 MB limit.`);
        setFiles((p) => ({ ...p, photo: shrunk }));
      } catch {
        setCompressing(false);
        setErrors(["We couldn't process that image. Please try a different photo."]);
      }
    }
  };

  const openCrop = () => { if (files.photo) setCropFile(files.photo); };
  const applyCrop = async (croppedFile) => {
    setCropFile(null);
    let finalFile = croppedFile;
    const MAX = 2 * 1024 * 1024;
    if (finalFile.size > MAX) {
      try { finalFile = await compressImage(finalFile, MAX); } catch { /* keep as-is */ }
    }
    setPhotoNote("Photo cropped to passport size (3:4).");
    setFiles((p) => ({ ...p, photo: finalFile }));
  };

  const saveDraft = async () => {
    if (!otp.verified) return;
    setSavingDraft(true);
    setDraftSavedNote("");
    try {
      await axios.post(`${API}/career-draft/save`, { email: f.email, data: { f, quals, exps } });
      setDraftSavedNote("Saved. When you return, verify this same email and we'll bring your answers back. (Re-attach your resume and photo then.)");
    } catch (err) {
      setErrors([err?.response?.data?.detail || "Could not save your draft right now. Please try again."]);
      document.getElementById("caf-top")?.scrollIntoView({ behavior: "smooth" });
    } finally {
      setSavingDraft(false);
    }
  };

  const restoreDraft = () => {
    const d = draftFound?.data || {};
    if (d.f) setF((p) => ({ ...p, ...d.f, email: f.email }));
    if (Array.isArray(d.quals) && d.quals.length) setQuals(d.quals);
    if (Array.isArray(d.exps) && d.exps.length) setExps(d.exps);
    setDraftFound(null);
    setDraftSavedNote("");
  };

  const discardDraft = async () => {
    setDraftFound(null);
    try { await axios.delete(`${API}/career-draft/${encodeURIComponent(f.email)}`); } catch { /* ignore */ }
  };

  const validate = () => {
    const errs = [];
    if (!otp.verified) errs.push("Verify your email address with the OTP sent to it before submitting.");
    if (!f.first_name.trim() || !f.surname.trim()) errs.push("First Name and Surname are required.");
    if (!emailValid(f.email)) errs.push("Enter a valid email address.");
    if (!f.father_name.trim() || !f.mother_name.trim()) errs.push("Father's and Mother's names are required.");
    if (!personalComplete) errs.push("Complete all required Personal Information fields (address, DOB, gender, marital status).");
    if (!/^\d{10}$/.test(f.mobile_number)) errs.push("Mobile number must be exactly 10 digits (numbers only).");
    if (!/^\d{10}$/.test(f.emergency_contact)) errs.push("Emergency contact number must be exactly 10 digits (numbers only).");
    if (f.emergency_contact && f.emergency_contact === f.mobile_number) errs.push("Emergency contact cannot be the same as the mobile number.");
    const q0 = quals[0];
    if (!q0.degree.trim() || !/^\d{4}$/.test(q0.year) || !q0.college.trim() || !/^\d{1,2}$/.test(q0.percentage)) errs.push("Complete the Qualification details (degree, 4-digit year, college, percentage).");
    if (!f.preferred_location_1) errs.push("Select Preferred Location 1.");
    if (!JOB_ROLES.includes(f.position)) errs.push("Select a Job Role.");
    if (!DEPARTMENTS.includes(f.department)) errs.push("Select a Department.");
    if (!f.experience_status) errs.push("Select Fresher or Experienced.");
    if (f.experience_status === "Experienced") {
      const e0 = exps[0];
      if (!e0.employer.trim() || !e0.designation.trim()) errs.push("Add at least one previous employer with designation.");
      exps.forEach((ex, i) => {
        if (!ex.from_month || !ex.from_year) errs.push(`Experience ${i + 1}: select Duration From month and year.`);
        if (!ex.current && (!ex.to_month || !ex.to_year)) errs.push(`Experience ${i + 1}: select Duration To month and year.`);
      });
      if (!f.current_ctc.trim() || !f.expected_ctc.trim()) errs.push("Current and Expected CTC are required for experienced candidates.");
      if (!f.notice_period) errs.push("Select your Notice Period.");
    }
    if (f.experience_status === "Fresher" && !f.expected_ctc.trim()) errs.push("Expected CTC is required.");
    if (!f.source.trim()) errs.push("Tell us how you heard about us.");
    if (!files.resume) errs.push("Upload your Resume (PDF).");
    if (!files.photo) errs.push("Upload your passport-size photo (JPG, JPEG or PNG).");
    if (!f.acknowledgement) errs.push("Please accept the declaration to submit.");
    if (!f.whatsapp_consent) errs.push("Please agree to WhatsApp contact to proceed.");
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (errs.length) {
      setErrors(errs);
      document.getElementById("caf-top")?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    // Re-check the required files are still attached right before sending.
    if (!files.resume || !files.photo) {
      setErrors([
        !files.resume
          ? "Your resume is no longer attached — please re-attach the PDF and submit again."
          : "Your passport photo is no longer attached — please re-attach the image and submit again.",
      ]);
      document.getElementById("caf-top")?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    setSending(true);
    setErrors([]);
    try {
      const payload = new FormData();
      Object.entries(f).forEach(([k, v]) => payload.append(k, String(v)));
      payload.append("qualifications", JSON.stringify(quals));
      payload.append("experiences", JSON.stringify(f.experience_status === "Experienced" ? exps : []));
      payload.append("resume_file", files.resume);
      payload.append("photo_file", files.photo);
      if (files.cover) payload.append("cover_letter_file", files.cover);
      const res = await axios.post(`${API}/career-application`, payload, { headers: { "Content-Type": "multipart/form-data" } });
      setDoneRef(res.data.reference_number);
    } catch (err) {
      setErrors(parseSubmitError(err));
      document.getElementById("caf-top")?.scrollIntoView({ behavior: "smooth" });
    } finally {
      setSending(false);
    }
  };

  const L = "block text-[11px] tracking-[0.16em] uppercase text-slate-400 mb-1.5";
  const REQ = <span className="text-red-400"> *</span>;
  const IN = "w-full bg-[#0e1b30] border border-white/15 text-slate-100 placeholder:text-slate-500 px-3.5 py-2.5 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors";
  const FS = "border border-white/10 bg-white/[0.02] p-5 sm:p-7 mb-6";
  const LG = "font-marcellus text-lg text-nx-goldlight mb-5";

  const stateBlock = (prefix) => {
    const c = f[`${prefix}_country`];
    if (c === "India") return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={L} htmlFor={`${prefix}-state`}>State{REQ}</label>
          <select id={`${prefix}-state`} data-testid={`caf-${prefix}-state`} className={IN} value={f[`${prefix}_state`]} onChange={set(`${prefix}_state`)}>
            <option value="">-- Select State --</option>
            {INDIA_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className={L} htmlFor={`${prefix}-pin`}>Pincode{REQ}</label>
          <input id={`${prefix}-pin`} data-testid={`caf-${prefix}-pincode`} className={IN} value={f[`${prefix}_pincode`]} onChange={setDigits(`${prefix}_pincode`)} maxLength={6} placeholder="6-digit pincode" />
        </div>
      </div>
    );
    if (c === "Other") return (
      <div className="grid sm:grid-cols-3 gap-4">
        <div><label className={L}>State / Region{REQ}</label><input data-testid={`caf-${prefix}-state-free`} className={IN} value={f[`${prefix}_state_free`]} onChange={setUpper(`${prefix}_state_free`)} /></div>
        <div><label className={L}>Country{REQ}</label><input data-testid={`caf-${prefix}-country-free`} className={IN} value={f[`${prefix}_country_free`]} onChange={setUpper(`${prefix}_country_free`)} /></div>
        <div><label className={L}>Pincode / ZIP{REQ}</label><input data-testid={`caf-${prefix}-pincode`} className={IN} value={f[`${prefix}_pincode`]} onChange={set(`${prefix}_pincode`)} /></div>
      </div>
    );
    return null;
  };

  return (
    <div className="fixed inset-0 z-[90] overflow-y-auto bg-[#0A1628]" data-lenis-prevent data-testid="career-application-modal">
      <div id="caf-top" className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="relative mb-6 text-center">
          <button onClick={onClose} className="absolute right-0 top-0 text-slate-400 hover:text-white border border-white/20 hover:border-nx-goldlight/60 p-2 transition-colors" aria-label="Close" data-testid="caf-close"><X size={20} /></button>
          <img src={LOGO.fullDark} alt="NexSecure Technologies" className="h-14 mx-auto mb-4" data-testid="caf-logo" />
          <h1 className="font-marcellus text-2xl sm:text-3xl text-white">Career Application Form</h1>
        </div>

        {doneRef ? (
          <div className="border border-nx-goldlight/40 bg-nx-goldlight/[0.06] p-8 text-center" data-testid="caf-success">
            <h2 className="font-marcellus text-2xl text-white mb-3">Application submitted.</h2>
            <p className="text-slate-300 text-sm mb-2">Your reference number:</p>
            <p className="text-2xl font-bold text-nx-goldlight tracking-widest mb-5" data-testid="caf-reference">{doneRef}</p>
            <p className="text-slate-400 text-sm leading-relaxed max-w-md mx-auto">Thank you for applying to NexSecure Technologies. We will review your application and respond within 5 working days. An acknowledgment email is on its way to your inbox.</p>
            <button onClick={onClose} className="mt-7 bg-nx-goldlight hover:bg-nx-gold text-nx-navy font-semibold text-sm px-8 py-3 transition-colors" data-testid="caf-done-close">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate data-testid="caf-form">
            {errors.length > 0 && (
              <div className="border border-red-500/60 bg-red-500/10 text-red-300 text-sm px-4 py-3 mb-6" data-testid="caf-errors">
                <b className="block mb-1">Please fix the following:</b>
                {errors.map((er, i) => <div key={i}>• {er}</div>)}
              </div>
            )}

            {draftFound && otp.verified && (
              <div className="border border-nx-goldlight/50 bg-nx-goldlight/[0.08] px-4 py-4 mb-6" data-testid="caf-draft-banner">
                <p className="text-sm text-white mb-1">We found a saved draft for this email.</p>
                <p className="text-[12px] text-slate-300 mb-3">Saved on {new Date(draftFound.saved_at).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}. Restoring brings back everything you'd typed — you'll just re-attach your resume and photo.</p>
                <div className="flex gap-3">
                  <button type="button" onClick={restoreDraft} className="bg-nx-goldlight hover:bg-nx-gold text-nx-navy font-semibold text-xs tracking-[0.12em] uppercase px-5 py-2.5 transition-colors" data-testid="caf-draft-restore">Restore my draft</button>
                  <button type="button" onClick={discardDraft} className="border border-white/20 text-slate-300 hover:text-white text-xs tracking-[0.12em] uppercase px-5 py-2.5 transition-colors" data-testid="caf-draft-discard">Start fresh</button>
                </div>
              </div>
            )}

            <fieldset className={FS}>
              <legend className={LG}>Personal Information</legend>
              <div className="mb-4">
                <label className={L} htmlFor="caf-email">Email Address{REQ}</label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input id="caf-email" data-testid="caf-email" type="email" className={IN} value={f.email} onChange={onEmailChange} onBlur={onEmailBlur} placeholder="you@email.com" />
                  {!otp.verified && (
                    <button type="button" onClick={sendOtp} disabled={otp.sending || otp.resendIn > 0 || !emailValid(f.email)} className="shrink-0 bg-nx-goldlight hover:bg-nx-gold disabled:opacity-50 disabled:cursor-not-allowed text-nx-navy font-semibold text-xs tracking-[0.14em] uppercase px-6 py-3 transition-colors" data-testid="caf-send-otp">
                      {otp.sending ? "Sending…" : otp.resendIn > 0 ? `Resend in ${otp.resendIn}s` : otp.sent ? "Resend OTP" : "Send OTP"}
                    </button>
                  )}
                </div>
                {inlineErr.email && <p className="text-[11px] text-red-400 mt-1.5" data-testid="caf-email-error">{inlineErr.email}</p>}
                {otp.verified ? (
                  <p className="flex items-center gap-2 text-[13px] text-emerald-400 mt-3" data-testid="caf-otp-verified"><Check size={15} /> Email verified — you can now complete the form.</p>
                ) : otp.sent ? (
                  <div className="mt-4 border border-nx-goldlight/30 bg-nx-goldlight/[0.05] p-4" data-testid="caf-otp-block">
                    <p className="text-[13px] text-slate-300 mb-3">A 6-digit verification code was sent to <span className="text-white">{f.email}</span>. It expires in 10 minutes.</p>
                    <div className="flex gap-3">
                      <input data-testid="caf-otp-input" inputMode="numeric" maxLength={6} className={`${IN} tracking-[0.4em] text-center font-mono text-lg sm:max-w-[180px]`} value={otp.code} onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ""); setOtp((p) => ({ ...p, code: v, error: "" })); if (v.length === 6) verifyOtp(v); }} placeholder="••••••" />
                      <button type="button" onClick={() => verifyOtp()} disabled={otp.verifying || otp.code.length !== 6} className="shrink-0 border border-nx-goldlight/60 text-nx-goldlight hover:bg-nx-goldlight/10 disabled:opacity-50 text-xs tracking-[0.14em] uppercase font-semibold px-6 transition-colors" data-testid="caf-verify-otp">{otp.verifying ? "Verifying…" : "Verify"}</button>
                    </div>
                    {otp.error && <p className="text-[11px] text-red-400 mt-2" data-testid="caf-otp-error">{otp.error}</p>}
                  </div>
                ) : (
                  <p className="text-[11px] text-slate-500 mt-1.5" data-testid="caf-otp-hint">Click "Send OTP" and verify your email to unlock the application form.</p>
                )}
              </div>
              <fieldset disabled={!otp.verified} className={otp.verified ? "" : "opacity-50"} data-testid="caf-locked-fields">
              <label className="flex items-start gap-3 cursor-pointer mb-6 border border-nx-goldlight/30 bg-nx-goldlight/[0.05] p-4" data-testid="caf-whatsapp-consent-label">
                <input type="checkbox" className="mt-1 accent-nx-goldlight" data-testid="caf-whatsapp-consent" checked={f.whatsapp_consent} onChange={set("whatsapp_consent")} />
                <span className="text-sm text-slate-200 leading-relaxed">I agree to be contacted by NexSecure Technologies through WhatsApp regarding my job application, interview, recruitment updates and related communication.{REQ}</span>
              </label>
              <div className="grid sm:grid-cols-3 gap-4 mb-4">
                <div><label className={L} htmlFor="caf-fn">First Name{REQ}</label><input id="caf-fn" data-testid="caf-first-name" className={IN} value={f.first_name} onChange={setUpper("first_name")} /></div>
                <div><label className={L} htmlFor="caf-mn">Middle Name</label><input id="caf-mn" data-testid="caf-middle-name" className={IN} value={f.middle_name} onChange={setUpper("middle_name")} /></div>
                <div><label className={L} htmlFor="caf-sn">Surname{REQ}</label><input id="caf-sn" data-testid="caf-surname" className={IN} value={f.surname} onChange={setUpper("surname")} /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div><label className={L}>Father&apos;s Name{REQ}</label><input data-testid="caf-father-name" className={IN} value={f.father_name} onChange={setUpper("father_name")} /></div>
                <div><label className={L}>Mother&apos;s Name{REQ}</label><input data-testid="caf-mother-name" className={IN} value={f.mother_name} onChange={setUpper("mother_name")} /></div>
              </div>
              <div className="mb-4"><label className={L}>Present Address{REQ}</label><textarea data-testid="caf-present-address" rows={2} className={IN} value={f.present_address} onChange={setUpper("present_address")} /></div>
              <div className="mb-2">
                <label className={L}>Present Country{REQ}</label>
                <select data-testid="caf-present-country" className={IN} value={f.present_country} onChange={set("present_country")}>
                  <option value="">-- Select Country --</option><option value="India">India</option><option value="Other">Other</option>
                </select>
              </div>
              {stateBlock("present")}
              <label className="flex items-center gap-2 text-sm text-slate-300 my-4 cursor-pointer" data-testid="caf-same-address-label">
                <input type="checkbox" className="accent-nx-goldlight" data-testid="caf-same-address" checked={f.same_address} onChange={set("same_address")} />
                Present address is same as Permanent address
              </label>
              <div className="mb-4"><label className={L}>Permanent Address{REQ}</label><textarea data-testid="caf-permanent-address" rows={2} className={IN} value={f.permanent_address} readOnly={f.same_address} onChange={setUpper("permanent_address")} /></div>
              <div className="mb-2">
                <label className={L}>Permanent Country{REQ}</label>
                <select data-testid="caf-permanent-country" className={IN} value={f.permanent_country} disabled={f.same_address} onChange={set("permanent_country")}>
                  <option value="">-- Select Country --</option><option value="India">India</option><option value="Other">Other</option>
                </select>
              </div>
              {!f.same_address && stateBlock("permanent")}
              <div className="grid sm:grid-cols-2 gap-4 mt-5">
                <div>
                  <label className={L}>Date of Birth{REQ}</label>
                  <div className="grid grid-cols-3 gap-2">
                    <select data-testid="caf-dob-day" className={IN} value={f.dob_day} onChange={set("dob_day")}><option value="">Day</option>{DAYS.map((d) => <option key={d} value={d}>{d}</option>)}</select>
                    <select data-testid="caf-dob-month" className={IN} value={f.dob_month} onChange={set("dob_month")}><option value="">Month</option>{MONTHS.map((m, i) => <option key={m} value={String(i + 1).padStart(2, "0")}>{m}</option>)}</select>
                    <select data-testid="caf-dob-year" className={IN} value={f.dob_year} onChange={set("dob_year")}><option value="">Year</option>{YEARS_DOB.map((y) => <option key={y} value={y}>{y}</option>)}</select>
                  </div>
                </div>
                <div>
                  <label className={L}>Gender{REQ}</label>
                  <div className="flex gap-5 pt-2.5">
                    {["Male", "Female", "Other"].map((g) => (
                      <label key={g} className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
                        <input type="radio" name="gender" className="accent-nx-goldlight" data-testid={`caf-gender-${g.toLowerCase()}`} checked={f.gender === g} onChange={() => setF((p) => ({ ...p, gender: g }))} /> {g}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mt-5">
                <div>
                  <label className={L}>Marital Status{REQ}</label>
                  <select data-testid="caf-marital-status" className={IN} value={f.marital_status} onChange={set("marital_status")}>
                    <option value="">-- Select Status --</option>{MARITAL.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                {f.marital_status === "Married" && (
                  <div><label className={L}>Spouse Name{REQ}</label><input data-testid="caf-spouse-name" className={IN} value={f.spouse_name} onChange={setUpper("spouse_name")} /></div>
                )}
              </div>
              <div className="mt-5"><label className={L}>Current Location{REQ}</label><input data-testid="caf-current-location" className={IN} value={f.current_location} onChange={setUpper("current_location")} placeholder="City / district" /></div>
            </fieldset>

            <fieldset className={FS}>
              <legend className={LG}>Contact Details</legend>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={L}>Mobile Number{REQ}</label>
                  <input data-testid="caf-mobile" type="tel" className={IN} value={f.mobile_number} onChange={setDigits("mobile_number")} onBlur={onPhoneBlur("mobile_number")} disabled={!personalComplete} placeholder={personalComplete ? "10-digit mobile" : "Complete Personal Info first"} maxLength={f.present_country === "Other" ? 15 : 10} />
                  {inlineErr.mobile_number ? <p className="text-[11px] text-red-400 mt-1" data-testid="caf-mobile-error">{inlineErr.mobile_number}</p> : <p className="text-[11px] text-slate-500 mt-1">{f.present_country === "Other" ? "Full number with country code" : "Enter 10-digit mobile number"}</p>}
                </div>
                <div>
                  <label className={L}>Emergency Contact Number{REQ}</label>
                  <input data-testid="caf-emergency" type="tel" className={IN} value={f.emergency_contact} onChange={setDigits("emergency_contact")} onBlur={onPhoneBlur("emergency_contact")} disabled={!personalComplete} placeholder={personalComplete ? "10-digit contact" : "Complete Personal Info first"} maxLength={f.present_country === "Other" ? 15 : 10} />
                  {inlineErr.emergency_contact ? <p className="text-[11px] text-red-400 mt-1" data-testid="caf-emergency-error">{inlineErr.emergency_contact}</p> : <p className="text-[11px] text-slate-500 mt-1">Must be different from your mobile number</p>}
                </div>
              </div>
            </fieldset>

            <fieldset className={FS}>
              <legend className={LG}>Qualification Details</legend>
              {quals.map((q, i) => (
                <div key={i} className="relative border border-white/10 p-4 mb-4" data-testid={`caf-qual-${i}`}>
                  {i > 0 && <button type="button" onClick={() => setQuals((x) => x.filter((_, j) => j !== i))} className="absolute top-3 right-3 text-red-400 text-xs border border-red-400/40 px-2 py-1 hover:bg-red-400/10" data-testid={`caf-qual-remove-${i}`}>Remove</button>}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><label className={L}>Highest Qualification / Degree{REQ}</label><input data-testid={`caf-qual-degree-${i}`} className={IN} value={q.degree} onChange={(e) => { e.target.value = e.target.value.toUpperCase(); setQual(i, "degree")(e); }} /></div>
                    <div><label className={L}>Year of Passing{REQ}</label><select data-testid={`caf-qual-year-${i}`} className={IN} value={q.year} onChange={setQual(i, "year")}><option value="">Year</option>{YEARS_EXP.map((y) => <option key={y} value={y}>{y}</option>)}</select></div>
                    <div><label className={L}>College / University Name{REQ}</label><input data-testid={`caf-qual-college-${i}`} className={IN} value={q.college} onChange={(e) => { e.target.value = e.target.value.toUpperCase(); setQual(i, "college")(e); }} /></div>
                    <div><label className={L}>Percentage / CGPA{REQ}</label><input data-testid={`caf-qual-percentage-${i}`} className={IN} maxLength={2} value={q.percentage} onChange={(e) => { e.target.value = e.target.value.replace(/[^0-9]/g, ""); setQual(i, "percentage")(e); }} /></div>
                  </div>
                  <div className="mt-4"><label className={L}>Additional Courses (optional)</label><textarea rows={2} className={IN} value={q.courses} onChange={(e) => { e.target.value = e.target.value.toUpperCase(); setQual(i, "courses")(e); }} /></div>
                </div>
              ))}
              <button type="button" onClick={() => setQuals((q) => [...q, { ...EMPTY_QUAL }])} className="text-nx-goldlight border border-nx-goldlight/50 hover:bg-nx-goldlight/10 text-sm px-5 py-2.5 transition-colors" data-testid="caf-add-qualification">+ Add Qualification</button>
            </fieldset>

            <fieldset className={FS}>
              <legend className={LG}>Job Preferences</legend>
              <div className="mb-4">
                <label className={L}>Preferred Location{REQ}</label>
                <select data-testid="caf-location-1" className={IN} value={f.preferred_location_1} onChange={set("preferred_location_1")}>
                  <option value="Ernakulam">Ernakulam</option>
                </select>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={L}>Job Role Applying For{REQ}</label>
                  <select data-testid="caf-job-role" className={IN} value={f.position} onChange={set("position")}>
                    <option value="">-- Select Role --</option>{JOB_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className={L}>Department{REQ}</label>
                  <select data-testid="caf-department" className={IN} value={f.department} onChange={set("department")}>
                    <option value="">-- Select Department --</option>{DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
            </fieldset>

            <fieldset className={FS}>
              <legend className={LG}>Experience Details</legend>
              <div className="flex gap-6 mb-5">
                {["Fresher", "Experienced"].map((x) => (
                  <label key={x} className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
                    <input type="radio" name="exp_status" className="accent-nx-goldlight" data-testid={`caf-exp-${x.toLowerCase()}`} checked={f.experience_status === x} onChange={() => setF((p) => ({ ...p, experience_status: x }))} /> {x}
                  </label>
                ))}
              </div>
              {f.experience_status === "Fresher" && (
                <div><label className={L}>Expected CTC (Annual in INR){REQ}</label><input data-testid="caf-expected-ctc" className={IN} placeholder="e.g. 300000" value={f.expected_ctc} onChange={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); set("expected_ctc")(e); }} /></div>
              )}
              {f.experience_status === "Experienced" && (
                <>
                  {exps.map((ex, i) => (
                    <div key={i} className="relative border border-white/10 p-4 mb-4" data-testid={`caf-exp-entry-${i}`}>
                      {i > 0 && <button type="button" onClick={() => setExps((x) => x.filter((_, j) => j !== i))} className="absolute top-3 right-3 text-red-400 text-xs border border-red-400/40 px-2 py-1 hover:bg-red-400/10" data-testid={`caf-exp-remove-${i}`}>Remove</button>}
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div><label className={L}>Employer{REQ}</label><input data-testid={`caf-employer-${i}`} className={IN} value={ex.employer} onChange={(e) => { e.target.value = e.target.value.toUpperCase(); setExp(i, "employer")(e); }} /></div>
                        <div><label className={L}>Designation{REQ}</label><input data-testid={`caf-designation-${i}`} className={IN} value={ex.designation} onChange={(e) => { e.target.value = e.target.value.toUpperCase(); setExp(i, "designation")(e); }} /></div>
                        <div>
                          <label className={L}>Duration From{REQ}</label>
                          <div className="grid grid-cols-2 gap-2">
                            <select data-testid={`caf-exp-from-month-${i}`} className={IN} value={ex.from_month} onChange={setExp(i, "from_month")}><option value="">Month</option>{MONTHS.map((m, j) => <option key={m} value={String(j + 1).padStart(2, "0")}>{m}</option>)}</select>
                            <select data-testid={`caf-exp-from-year-${i}`} className={IN} value={ex.from_year} onChange={setExp(i, "from_year")}><option value="">Year</option>{YEARS_EXP.map((y) => <option key={y} value={y}>{y}</option>)}</select>
                          </div>
                        </div>
                        <div>
                          <label className={L}>Duration To{REQ}</label>
                          <div className="grid grid-cols-2 gap-2">
                            <select data-testid={`caf-exp-to-month-${i}`} className={IN} value={ex.to_month} disabled={ex.current} onChange={setExp(i, "to_month")}><option value="">Month</option>{MONTHS.map((m, j) => <option key={m} value={String(j + 1).padStart(2, "0")}>{m}</option>)}</select>
                            <select data-testid={`caf-exp-to-year-${i}`} className={IN} value={ex.to_year} disabled={ex.current} onChange={setExp(i, "to_year")}><option value="">Year</option>{YEARS_EXP.map((y) => <option key={y} value={y}>{y}</option>)}</select>
                          </div>
                          <label className="flex items-center gap-2 text-xs text-slate-400 mt-2 cursor-pointer">
                            <input type="checkbox" className="accent-nx-goldlight" data-testid={`caf-exp-current-${i}`} checked={ex.current} onChange={setExp(i, "current")} /> Currently working here
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button type="button" onClick={() => setExps((x) => [...x, { ...EMPTY_EXP }])} className="text-nx-goldlight border border-nx-goldlight/50 hover:bg-nx-goldlight/10 text-sm px-5 py-2.5 mb-5 transition-colors" data-testid="caf-add-experience">+ Add Previous Employer</button>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><label className={L}>Current CTC (Annual in INR){REQ}</label><input data-testid="caf-current-ctc" className={IN} placeholder="e.g. 500000" value={f.current_ctc} onChange={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); set("current_ctc")(e); }} /></div>
                    <div><label className={L}>Expected CTC (Annual in INR){REQ}</label><input data-testid="caf-expected-ctc-exp" className={IN} placeholder="e.g. 600000" value={f.expected_ctc} onChange={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); set("expected_ctc")(e); }} /></div>
                  </div>
                  <div className="mt-4">
                    <label className={L}>Notice Period{REQ}</label>
                    <select data-testid="caf-notice-period" className={IN} value={f.notice_period} onChange={set("notice_period")}>
                      <option value="">-- Select Duration --</option>{NOTICE.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </>
              )}
              <div className="mt-4"><label className={L}>How did you hear about us?{REQ}</label><input data-testid="caf-source" className={IN} value={f.source} onChange={setUpper("source")} placeholder="e.g. LinkedIn, referral, job portal" /></div>
            </fieldset>

            <fieldset className={FS}>
              <legend className={LG}>Document Upload</legend>
              {[
                { key: "resume", label: "Upload Resume (PDF only, max 5MB)", req: true, accept: ".pdf", testid: "caf-resume" },
                { key: "photo", label: "Upload Passport Size Photo (JPG, JPEG or PNG, max 2MB)", req: true, accept: ".jpg,.jpeg,.png", testid: "caf-photo" },
                { key: "cover", label: "Upload Cover Letter (PDF only, max 5MB — optional)", req: false, accept: ".pdf", testid: "caf-cover" },
              ].map((u) => (
                <div key={u.key} className="mb-4">
                  <label className={L}>{u.label}{u.req && REQ}</label>
                  {files[u.key] ? (
                    <>
                      <div className="flex items-center gap-3 border border-nx-goldlight/40 bg-nx-goldlight/[0.06] px-4 py-3" data-testid={`${u.testid}-attached`}>
                        {u.key === "photo" && photoUrl && (
                          <img src={photoUrl} alt="Passport photo preview" className="h-12 w-12 rounded object-cover border border-white/15 shrink-0" data-testid="caf-photo-preview" />
                        )}
                        <span className="text-sm text-slate-200 truncate flex-1">{files[u.key].name}</span>
                        {u.key === "photo" && <span className="text-[11px] text-slate-400 shrink-0">{fmtSize(files[u.key].size)}</span>}
                        <button type="button" onClick={() => setFiles((p) => ({ ...p, [u.key]: null }))} className="text-slate-400 hover:text-white p-1 shrink-0" data-testid={`${u.testid}-remove`}><X size={15} /></button>
                      </div>
                      {u.key === "photo" && photoNote && <p className="text-[11px] text-emerald-400 mt-1.5" data-testid="caf-photo-note">{photoNote}</p>}
                      {u.key === "photo" && (
                        <button type="button" onClick={openCrop} className="mt-2 text-[11px] tracking-[0.1em] uppercase text-nx-goldlight border border-nx-goldlight/50 hover:bg-nx-goldlight/10 px-3 py-1.5 transition-colors" data-testid="caf-crop-open">Crop to passport size (3:4)</button>
                      )}
                    </>
                  ) : (
                    <label className={`flex items-center justify-center gap-3 border border-dashed px-4 py-5 text-sm transition-colors ${u.key === "photo" && compressing ? "border-nx-goldlight/50 text-slate-300 cursor-wait" : "border-white/20 hover:border-nx-goldlight/50 text-slate-400 hover:text-slate-200 cursor-pointer"}`} data-testid={`${u.testid}-upload`}>
                      {u.key === "photo" && compressing ? (
                        <><Loader2 size={16} className="text-nx-goldlight animate-spin" /> Optimising photo…</>
                      ) : (
                        <><UploadCloud size={16} className="text-nx-goldlight" /> Choose file</>
                      )}
                      <input type="file" accept={u.accept} className="hidden" onChange={pickFile(u.key)} disabled={u.key === "photo" && compressing} data-testid={`${u.testid}-input`} />
                    </label>
                  )}
                </div>
              ))}
            </fieldset>

            <label className="flex items-start gap-3 cursor-pointer mb-6" data-testid="caf-declaration-label">
              <input type="checkbox" className="mt-1 accent-nx-goldlight" data-testid="caf-declaration" checked={f.acknowledgement} onChange={set("acknowledgement")} />
              <span className="text-sm text-slate-300 leading-relaxed">I hereby confirm that all information provided is true to the best of my knowledge.{REQ}</span>
            </label>

            <button type="submit" disabled={sending} className="w-full bg-nx-goldlight hover:bg-nx-gold text-nx-navy font-bold tracking-wide text-base px-10 py-4 transition-colors disabled:opacity-60" data-testid="caf-submit">
              {sending ? "Submitting…" : "Submit Application"}
            </button>
            <button type="button" onClick={saveDraft} disabled={savingDraft} className="w-full mt-3 border border-nx-goldlight/50 text-nx-goldlight hover:bg-nx-goldlight/10 text-sm tracking-wide px-10 py-3 transition-colors disabled:opacity-60" data-testid="caf-save-draft">
              {savingDraft ? "Saving…" : "Save & finish later"}
            </button>
            {draftSavedNote && <p className="text-[12px] text-emerald-400 mt-2 text-center" data-testid="caf-draft-saved-note">{draftSavedNote}</p>}
              </fieldset>
          </form>
        )}
      </div>
      {cropFile && <PassportCropModal file={cropFile} onCancel={() => setCropFile(null)} onApply={applyCrop} />}
    </div>
  );
};

export default CareerApplicationForm;
